/**
 * useSrAnnouncement — app-level helper for screen-reader announcements.
 *
 * Per `~/Desktop/design_handoff_properties/ACCESSIBILITY.md` §6 +
 * CLAUDE.md "Frontend (Sparta)" non-negotiable rule §7 (WCAG 2.1 AA
 * with aria-live), several FE surfaces need to announce state
 * changes that a sighted user notices but a screen-reader user
 * would miss — view-toggle changes, filter results, wizard step
 * advances, status saves, AI generation states.
 *
 * The hook returns:
 *   - `polite(message)` — schedules a polite announcement (announced
 *     when the user is idle; right for status updates, results
 *     counts, navigation changes)
 *   - `assertive(message)` — schedules an assertive announcement
 *     (interrupts current SR output; right for validation errors,
 *     AI failures, urgent state)
 *   - `regions` — JSX with two visually-hidden `<div>` elements
 *     (`aria-live="polite"` + `aria-live="assertive"`) that the
 *     caller renders once inside its layout tree
 *
 * Implementation notes:
 *   - Announcements use `aria-atomic="true"` so the whole text is
 *     read on update (not just the diff).
 *   - The regions stay in the DOM even when empty so screen readers
 *     observe the same node across renders — appending+removing
 *     `aria-live` nodes causes some SR engines to miss the first
 *     announcement.
 *   - Repeated identical announcements use a counter suffix that
 *     stays hidden (`aria-hidden="true"`) — SRs re-announce only
 *     when the visible text changes, so "Showing 5 of 12" followed
 *     by another "Showing 5 of 12" won't fire twice unless we
 *     force a diff.
 *
 * The hook lives in `apps/admin/src/shared/hooks/` because it's
 * app-local (not a kit primitive) and used across modules
 * (Properties today; future Reservations/Housekeeping). See ADR
 * `frontend-0001` Hybrid Z layering.
 */

import * as React from 'react';

export interface SrAnnouncement {
  /** Schedule a polite announcement (announced when SR is idle). */
  readonly polite: (message: string) => void;
  /**
   * Schedule an assertive announcement (interrupts current SR
   * output). Reserve for validation errors, AI failures, urgent
   * states — per ACCESSIBILITY.md §6.
   */
  readonly assertive: (message: string) => void;
  /**
   * JSX containing the two visually-hidden live regions. Caller
   * must render this once inside its layout tree.
   */
  readonly regions: React.ReactElement;
}

export function useSrAnnouncement(): SrAnnouncement {
  // `count` forces a diff on repeated-identical announcements so
  // screen readers re-announce. Without it, calling `polite("Saved")`
  // twice in a row produces a single SR utterance.
  const [politeMessage, setPoliteMessage] = React.useState<{
    text: string;
    nonce: number;
  }>({ text: '', nonce: 0 });
  const [assertiveMessage, setAssertiveMessage] = React.useState<{
    text: string;
    nonce: number;
  }>({ text: '', nonce: 0 });

  const polite = React.useCallback((message: string) => {
    setPoliteMessage((prev) => ({ text: message, nonce: prev.nonce + 1 }));
  }, []);
  const assertive = React.useCallback((message: string) => {
    setAssertiveMessage((prev) => ({ text: message, nonce: prev.nonce + 1 }));
  }, []);

  const regions = (
    <>
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {politeMessage.text}
        {/* The nonce stays SR-hidden but ensures the text node
            actually changes when the same message is announced
            twice — some SRs deduplicate identical text and skip the
            second utterance otherwise. */}
        <span aria-hidden="true">
          {politeMessage.nonce > 0 ? ` ${politeMessage.nonce}` : ''}
        </span>
      </div>
      <div
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
        className="sr-only"
      >
        {assertiveMessage.text}
        <span aria-hidden="true">
          {assertiveMessage.nonce > 0 ? ` ${assertiveMessage.nonce}` : ''}
        </span>
      </div>
    </>
  );

  return { polite, assertive, regions };
}
